<template>
  <!--
    Keep a single element root. Root `v-if` SSRs a comment when empty, which
    breaks Lazy + hydrate-on-visible (IntersectionObserver needs an Element).
  -->
  <div
    v-bind="
      visibleFaqItems.length
        ? { itemscope: '', itemtype: 'https://schema.org/FAQPage' }
        : {}
    "
  >
    <template v-for="entry in visibleEntries" :key="entry.type">
      <EventIncluded
        v-if="entry.kind === 'structuredIncluded'"
        :included="structuredIncluded"
        :event-data="eventData"
        :is-giftcard="isGiftcard"
      />

      <div
        v-else
        :data-id="entry.type"
        class="border-t border-gray-200 py-7"
        itemprop="mainEntity"
        itemscope
        itemtype="https://schema.org/Question"
      >
        <div class="flex items-center">
          <component
            :is="entry.icon"
            class="mt-0.5 size-5 shrink-0 text-teal-600"
            aria-hidden="true"
          />
          <div class="ml-4 min-w-0">
            <h3
              class="text-base font-semibold leading-6 text-stone-800"
              itemprop="name"
            >
              {{ entry.title }}
            </h3>
          </div>
        </div>

        <div
          class="mt-4 text-sm !leading-5 text-stone-600"
          itemprop="acceptedAnswer"
          itemscope
          itemtype="https://schema.org/Answer"
        >
          <!--
            Preamble is raw CMS markup (usually its own `<p>`), so it must go
            into a block container — nesting it in a `<p>` produces invalid
            markup the browser silently repairs, splitting it away from the
            list and from `itemprop="text"`. One wrapper carries `itemprop`
            so the answer text stays a single value.
          -->
          <div v-if="entry.listItems?.length" itemprop="text">
            <div
              v-if="entry.listPreambleHtml"
              class="mb-2 space-y-2"
              v-html="entry.listPreambleHtml"
            />
            <ul
              class="list-disc space-y-2 pl-5"
              :data-test-id="`event-important-details-${entry.type}-list`"
            >
              <li
                v-for="(listItem, listItemIndex) in entry.listItems"
                :key="listItemIndex"
                v-html="listItem"
              />
            </ul>
            <div
              v-if="entry.listAfterHtml"
              class="mt-2 space-y-2"
              v-html="entry.listAfterHtml"
            />
          </div>
          <HtmlReadMore
            v-else
            :html="entry.description"
            :default-expanded="entry.type === 'included' && isBoxEvent"
            :test-id-prefix="`event-important-details-${entry.type}`"
            itemprop="text"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import {
  FileText,
  Gift,
  Info,
  ListChecks,
  PencilRuler,
  UserCheck,
} from 'lucide-vue-next'
import { useTitleByCategory } from '~/composables/product/useGetTitleByCategory'
import type { EventDescription } from '~/composables/types/event'
import { parseHtmlList } from '~/utils/parseHtmlList'
import {
  EVENT_IMPORTANT_DETAIL_ALL_ITEMS,
  type EventImportantDetailSlug,
} from '~/utils/eventImportantDetails'

interface ImportantDetailItem {
  type: EventImportantDetailSlug
  icon: Component
  title: string
  description: string
  /** Set when `description` is CMS list markup — rendered as a plain bullet
   *  list (matching the structured "included" block) instead of HtmlReadMore. */
  listItems?: string[] | null
  /** Intro HTML preceding the list (e.g. "Please bring the following:"). */
  listPreambleHtml?: string | null
  /** Closing HTML following the list (e.g. a final remark). */
  listAfterHtml?: string | null
}

/**
 * Rendered rows: rich-text FAQ entries plus the structured "included" block,
 * both ordered by the `items` prop so slugs drive the display order.
 */
type ImportantDetailEntry =
  | { kind: 'structuredIncluded'; type: 'included' }
  | ({ kind: 'faq' } & ImportantDetailItem)

const { getTitleByCategory } = useTitleByCategory()

const {
  eventData = {} as EventDescription,
  isGiftcard = false,
  items = EVENT_IMPORTANT_DETAIL_ALL_ITEMS,
} = defineProps<{
  eventData?: EventDescription
  isGiftcard?: boolean
  /** Slugs of sections to render, in display order. Defaults to all. */
  items?: EventImportantDetailSlug[]
}>()

const isBoxEvent = computed(() => eventGetters.isBoxEvent(eventData))
const isGiftcardContext = computed(
  () => isGiftcard || eventGetters.isGiftcardEvent(eventData)
)

const structuredIncluded = computed(() => eventGetters.getIncluded(eventData))
const hasStructuredIncluded = computed(
  () => structuredIncluded.value.length > 0
)

/** API rich-text fields are typed string but may be null/non-string at runtime. */
const asHtmlDescription = (value: unknown): string =>
  typeof value === 'string' ? value : ''

/**
 * Builds a plain FAQ item, always rendered via HtmlReadMore prose.
 */
const makeItem = (
  type: ImportantDetailItem['type'],
  icon: Component,
  title: string,
  rawDescription: unknown
): ImportantDetailItem => ({
  type,
  icon,
  title,
  description: asHtmlDescription(rawDescription),
})

/**
 * Builds a FAQ item and, when its CMS content is list markup (`<ul>/<ol>`),
 * pre-parses it into plain list items (plus any intro text before the list)
 * so the template can render a bullet list matching the structured
 * "included" block instead of HtmlReadMore prose.
 */
const makeListAwareItem = (
  type: ImportantDetailItem['type'],
  icon: Component,
  title: string,
  rawDescription: unknown
): ImportantDetailItem => {
  const description = asHtmlDescription(rawDescription)
  const parsed = parseHtmlList(description)
  return {
    type,
    icon,
    title,
    description,
    listItems: parsed?.items,
    listPreambleHtml: parsed?.preambleHtml,
    listAfterHtml: parsed?.afterHtml,
  }
}

const allFaqItems = computed<ImportantDetailItem[]>(() => {
  const candidates: ImportantDetailItem[] = [
    isGiftcardContext.value
      ? makeItem(
          'giftcard',
          Gift,
          getTitleByCategory('about', eventData, isGiftcard),
          eventGetters.getDescription(eventData)
        )
      : makeItem(
          'about',
          Info,
          getTitleByCategory('about', eventData, isGiftcard),
          eventGetters.getDescription(eventData)
        ),
    makeListAwareItem(
      'steps',
      ListChecks,
      getTitleByCategory('progress', eventData, isGiftcard),
      eventGetters.getCourseProgress(eventData)
    ),
    ...(!hasStructuredIncluded.value
      ? [
          makeListAwareItem(
            'included',
            PencilRuler,
            getTitleByCategory('whatIsIncluded', eventData, isGiftcard),
            eventGetters.getWhatIsIncluded(eventData)
          ),
        ]
      : []),
    makeListAwareItem(
      'bring',
      FileText,
      getTitleByCategory('whatToBring', eventData, isGiftcard),
      eventGetters.getWhatToBring(eventData)
    ),
    ...(asHtmlDescription(eventGetters.getCourseTarget(eventData))
      ? [
          makeListAwareItem(
            'courseTarget',
            UserCheck,
            getTitleByCategory('courseTarget', eventData, isGiftcard),
            eventGetters.getCourseTarget(eventData)
          ),
        ]
      : []),
  ]

  return candidates.filter((item) => item.description.trim().length > 0)
})

const visibleEntries = computed<ImportantDetailEntry[]>(() => {
  const byType = new Map(
    allFaqItems.value.map((item) => [item.type, item] as const)
  )

  return items.flatMap<ImportantDetailEntry>((slug) => {
    if (slug === 'included' && hasStructuredIncluded.value) {
      return [{ kind: 'structuredIncluded', type: 'included' }]
    }

    const item = byType.get(slug)

    return item ? [{ kind: 'faq', ...item }] : []
  })
})

const visibleFaqItems = computed(() =>
  visibleEntries.value.filter((entry) => entry.kind === 'faq')
)
</script>
